/**
 * The catalogue, filtered — one question in, one answer out: what the rail shows, and what every
 * facet chip says beside its own label.
 *
 * Ported from `move-with-me-frontend/.../features/exercises/lib/exerciseFilters.ts`, whose facet
 * semantics are the part that must not diverge: **within a facet the selected values are OR'd,
 * across facets they are AND'd.** A trainer who ticks "Plecy" and "Ramiona" means either; a trainer
 * who ticks "Plecy" and "Hantle" means both.
 *
 * ── One entry point, not five predicates ─────────────────────────────────────────────────────
 * Mobile exports `applyExerciseFilters`, `countActiveFilters`, `deriveEquipmentOptions` and
 * `deriveMuscleOptions` separately, and its screen composes them. That composition IS a decision —
 * which options exist, in what order, with what counts, over which list — so it lives behind
 * `exerciseCatalogue` here rather than in a component where no test can reach it. The rail reads
 * one value and renders it.
 *
 * ── The counts are over the WHOLE catalogue, deliberately ────────────────────────────────────
 * Not over the current result set. A chip whose count shrinks as the trainer ticks other chips
 * cannot be read as "how much of the library is this" — it becomes a moving number that says
 * nothing on its own, and it makes an unticked chip read as empty the moment an unrelated facet
 * narrows past it. The count answers a fixed question: how many movements in this library carry
 * this value. What DOES narrow is the result list, which is what the trainer is looking at.
 *
 * ── Search is the backend's rule, replayed ───────────────────────────────────────────────────
 * Every word of the term must occur inside ONE of a movement's names — the displayed one, the other
 * content language's, any alias in any language, or one of the trainer's own tags — compared
 * through `exerciseName`'s normalisation: case, diacritics and punctuation set aside, `ł` read as
 * `l` (exercise-library R4). The catalogue is authored in Polish, so a trainer typing "przysiad ze
 * sztanga" on a keyboard they have not switched must still reach "Przysiad ze sztangą"; and the
 * server applies the same rule for the phone, so the two clients find the same movements for the
 * same term. A fixture the backend emits keeps them there (`exerciseNameParity.test.ts`).
 *
 * ── The order is COLLATED, never `localeCompare` per pair ────────────────────────────────────
 * One `Intl.Collator` built once per call and reused across the sort. `String.localeCompare` builds
 * a collator per comparison, which on a 257-row catalogue is several thousand of them per
 * keystroke. `sensitivity: 'base'` so "Ćwiczenie" sorts where a Polish reader expects it and case
 * never decides an ordering.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { normaliseExerciseName, searchWords, wordsMatchOneName } from './exerciseName';
import { EXERCISE_TYPE_KEYS, type ExerciseTypeKey } from './exerciseType';
import type { ExerciseEquipment, ExerciseListItem } from './exerciseListItem';
import { ALL_MUSCLE_GROUPS, type MuscleGroup } from './muscleGroup';

/** Where a movement came from: the shared catalogue, or this trainer's own. */
export type ExerciseOrigin = 'all' | 'mine' | 'library';

/** The origins the facet offers, in the order it lists them. `all` leads because it is the default. */
export const EXERCISE_ORIGINS: readonly ExerciseOrigin[] = ['all', 'mine', 'library'];

/**
 * What the trainer has asked of the catalogue.
 *
 * `text` is raw — exactly what is in the search field, untrimmed and unfolded — because the folding
 * is this module's job and doing it at the input would mean the field's own value and the value
 * being matched are two different strings.
 */
export interface ExerciseQuery {
  readonly text: string;
  readonly muscles: readonly MuscleGroup[];
  readonly equipment: readonly number[];
  readonly types: readonly ExerciseTypeKey[];
  readonly origin: ExerciseOrigin;
  /**
   * The trainer's own tags, by their NORMALISED form — a tag's identity (R22), so a selection
   * survives the spelling it is shown in. OR'd within, AND'd with every other facet (R23).
   */
  readonly tags: readonly string[];
}

/** Nothing asked: the whole catalogue, in name order. */
export function emptyExerciseQuery(): ExerciseQuery {
  return { text: '', muscles: [], equipment: [], types: [], origin: 'all', tags: [] };
}

/** One chip: a value, how many movements carry it, and whether it is currently selected. */
export interface ExerciseFacetOption<Value> {
  readonly value: Value;
  readonly count: number;
  readonly isSelected: boolean;
}

/** The equipment facet's chips carry the equipment's own name, because it is backend data. */
export interface EquipmentFacetOption extends ExerciseFacetOption<number> {
  readonly name: string;
  /** The piece's photograph, or `null`. Backend data, like the name beside it. */
  readonly imageUrl: string | null;
}

/** One of the trainer's tags: its identity, the spelling it is shown in, and how many carry it. */
export interface ExerciseTagOption extends ExerciseFacetOption<string> {
  readonly label: string;
}

export interface ExerciseCatalogueView {
  /** What the rail lists, filtered and collated. */
  readonly results: readonly ExerciseListItem[];
  /** How many movements the catalogue holds in total — the denominator the results are a slice of. */
  readonly total: number;
  readonly muscles: readonly ExerciseFacetOption<MuscleGroup>[];
  readonly equipment: readonly EquipmentFacetOption[];
  readonly types: readonly ExerciseFacetOption<ExerciseTypeKey>[];
  readonly origins: readonly ExerciseFacetOption<ExerciseOrigin>[];
  /**
   * Every tag the trainer has put on a movement in this catalogue, by label. Empty for a trainer
   * with none — which is what hides the tag bar and the tag column.
   */
  readonly tags: readonly ExerciseTagOption[];
  /**
   * How many facet selections are active, counting `origin` as one when it is not `all`. Drives the
   * "clear filters" affordance, which must not appear when there is nothing to clear.
   */
  readonly activeCount: number;
}

/**
 * A movement's name in the OTHER content language, by id — what lets a search find it in either.
 *
 * The phone's library searches Polish and English names at once on the server (an `ILIKE` over
 * both); this catalogue is read whole in one language and filtered here, so the second language's
 * names arrive as this map. Empty while that read is pending or after it failed, and the search then
 * answers in the displayed language alone — never an error, never a wait.
 */
export type OtherLanguageNames = ReadonlyMap<number, string>;

const NO_OTHER_NAMES: OtherLanguageNames = new Map();

/**
 * Does the movement match the search?
 *
 * Its names are the displayed one, the other content language's, every alias in every language
 * (R18) and every tag the trainer put on it (R21). Aliases and tags FIND a movement; nothing here
 * changes the name it is listed under.
 */
function matchesText(
  exercise: ExerciseListItem,
  words: readonly string[],
  otherName: string | undefined,
): boolean {
  if (words.length === 0) return true;
  return wordsMatchOneName(words, [
    exercise.name,
    ...(otherName === undefined ? [] : [otherName]),
    ...exercise.aliases.map((alias) => alias.name),
    ...exercise.tags,
  ]);
}

/**
 * The search rule on a bare NAME, for a host holding movements the catalogue has not described:
 * the same normalisation and the same every-word rule, so a name reads the same whichever side
 * answers it.
 */
export function exerciseNameMatches(name: string, text: string): boolean {
  return wordsMatchOneName(searchWords(text), [name]);
}

/** Any of the selected tags — OR within the facet, as every other facet is. */
function matchesTags(exercise: ExerciseListItem, keys: ReadonlySet<string>): boolean {
  if (keys.size === 0) return true;
  return exercise.tags.some((label) => keys.has(normaliseExerciseName(label)));
}

/**
 * Every tag in the catalogue, de-duplicated by its normalised form.
 *
 * The backend keeps one spelling per normalised tag per trainer (R22), so the first spelling the
 * walk meets is the only one there is; taking it deterministically still beats a chip whose label
 * depends on which movement was reached first, should the two ever disagree.
 */
function distinctTags(items: readonly ExerciseListItem[]): Map<string, { label: string; count: number }> {
  const byKey = new Map<string, { label: string; count: number }>();
  for (const item of items) {
    const seen = new Set<string>();
    for (const label of item.tags) {
      const key = normaliseExerciseName(label);
      if (key === '' || seen.has(key)) continue;
      seen.add(key);
      const entry = byKey.get(key);
      if (entry === undefined) byKey.set(key, { label, count: 1 });
      else entry.count += 1;
    }
  }
  return byKey;
}

function matchesOrigin(exercise: ExerciseListItem, origin: ExerciseOrigin): boolean {
  if (origin === 'mine') return exercise.isCustom;
  if (origin === 'library') return !exercise.isCustom;
  return true;
}

function matchesTypes(exercise: ExerciseListItem, types: readonly ExerciseTypeKey[]): boolean {
  if (types.length === 0) return true;
  return exercise.typeKey !== null && types.includes(exercise.typeKey);
}

/**
 * PRIMARY ∪ SECONDARY, which is mobile's rule and the honest one: a movement that works the back as
 * a secondary is still a movement a trainer filtering for back work wants offered. Narrowing to the
 * primary would hide every row and pull-down from the "Plecy" facet.
 */
function worksMuscle(exercise: ExerciseListItem, muscles: readonly MuscleGroup[]): boolean {
  if (muscles.length === 0) return true;
  return muscles.some(
    (muscle) =>
      exercise.primaryMuscles.includes(muscle) || exercise.secondaryMuscles.includes(muscle),
  );
}

function matchesEquipment(exercise: ExerciseListItem, ids: readonly number[]): boolean {
  if (ids.length === 0) return true;
  return exercise.equipment.some((item) => ids.includes(item.id));
}

/** Count how many movements in the whole catalogue satisfy a predicate. */
function countBy(
  items: readonly ExerciseListItem[],
  predicate: (item: ExerciseListItem) => boolean,
): number {
  let count = 0;
  for (const item of items) if (predicate(item)) count += 1;
  return count;
}

/**
 * Every piece of equipment named anywhere in the catalogue, de-duplicated by id.
 *
 * The NAME of the first occurrence wins. Two rows sharing an id and disagreeing on the name is a
 * backend inconsistency this cannot resolve, and picking one deterministically beats rendering the
 * chip's label differently depending on which exercise the walk reached first.
 */
function distinctEquipment(items: readonly ExerciseListItem[]): ExerciseEquipment[] {
  const byId = new Map<number, ExerciseEquipment>();
  for (const item of items) {
    for (const piece of item.equipment) {
      if (!byId.has(piece.id)) byId.set(piece.id, piece);
    }
  }
  return [...byId.values()];
}

/**
 * The catalogue, answered.
 *
 * @param items The whole library. Facet counts are taken over THIS, never over the results.
 * @param query What the trainer asked.
 * @param locale The collation locale — Polish orders `ą` `ć` `ę` `ł` `ń` `ó` `ś` `ź` `ż` where
 *   English does not, and a rail sorted by code point puts every Polish name in the wrong place.
 * @param otherNames The same movements' names in the other content language. The search matches
 *   them as well; nothing else reads them — the list is still drawn and collated by `name`.
 */
export function exerciseCatalogue(
  items: readonly ExerciseListItem[],
  query: ExerciseQuery,
  locale: string,
  otherNames: OtherLanguageNames = NO_OTHER_NAMES,
): ExerciseCatalogueView {
  const words = searchWords(query.text);
  const tagsByKey = distinctTags(items);
  // A selected tag no movement carries any more — renamed or deleted under the selection — narrows
  // nothing. Kept, it would empty the list behind a chip the bar no longer draws.
  const selectedTags = new Set(query.tags.filter((key) => tagsByKey.has(key)));

  const results = items.filter(
    (item) =>
      matchesText(item, words, otherNames.get(item.id)) &&
      matchesTags(item, selectedTags) &&
      matchesOrigin(item, query.origin) &&
      matchesTypes(item, query.types) &&
      worksMuscle(item, query.muscles) &&
      matchesEquipment(item, query.equipment),
  );

  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  results.sort((left, right) => collator.compare(left.name, right.name));

  // A facet only ever lists values the catalogue actually holds, in the taxonomy's own order. A chip
  // reading "Łydki 0" invites a click that can only empty the list, so it is not offered at all.
  const muscles = ALL_MUSCLE_GROUPS.map((muscle) => ({
    value: muscle,
    count: countBy(items, (item) => worksMuscle(item, [muscle])),
    isSelected: query.muscles.includes(muscle),
  })).filter((option) => option.count > 0);

  const equipment = distinctEquipment(items)
    .map((piece) => ({
      value: piece.id,
      name: piece.name,
      imageUrl: piece.imageUrl,
      count: countBy(items, (item) => matchesEquipment(item, [piece.id])),
      isSelected: query.equipment.includes(piece.id),
    }))
    .sort((left, right) => collator.compare(left.name, right.name));

  const types = EXERCISE_TYPE_KEYS.map((type) => ({
    value: type,
    count: countBy(items, (item) => matchesTypes(item, [type])),
    isSelected: query.types.includes(type),
  })).filter((option) => option.count > 0);

  // Origin is single-select and `all` is always offered, count or no count: it is how a trainer
  // gets back, and a facet whose reset option disappears when it is empty is a trap.
  const origins = EXERCISE_ORIGINS.map((origin) => ({
    value: origin,
    count: countBy(items, (item) => matchesOrigin(item, origin)),
    isSelected: query.origin === origin,
  }));

  const tags = [...tagsByKey.entries()]
    .map(([key, entry]) => ({
      value: key,
      label: entry.label,
      count: entry.count,
      isSelected: selectedTags.has(key),
    }))
    .sort((left, right) => collator.compare(left.label, right.label));

  return {
    results,
    total: items.length,
    muscles,
    equipment,
    types,
    origins,
    tags,
    activeCount:
      query.muscles.length +
      query.equipment.length +
      query.types.length +
      selectedTags.size +
      (query.origin === 'all' ? 0 : 1),
  };
}

/**
 * Add or remove one value from a multi-select facet — the toggle every chip performs.
 *
 * Here rather than in the hook because "a second click clears" is a rule, and because the two
 * facets that are lists of numbers and the two that are lists of strings would otherwise each grow
 * their own copy of it.
 */
export function toggleFacetValue<Value>(
  selected: readonly Value[],
  value: Value,
): Value[] {
  return selected.includes(value)
    ? selected.filter((candidate) => candidate !== value)
    : [...selected, value];
}
